"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MultiSig = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const MultiSigProposal_1 = require("../../MultiSigProposal");
const setMultiSigAdmin_1 = require("../../../procedures/setMultiSigAdmin");
const internal_1 = require("../../../../internal");
const multisigs_1 = require("../../../../middleware/queries/multisigs");
const types_1 = require("../../../../types");
const conversion_1 = require("../../../../utils/conversion");
const internal_2 = require("../../../../utils/internal");
/**
 * Represents a MultiSig Account. A MultiSig Account is composed of one or more signing Accounts. In order to submit a transaction, a specific amount of those signing Accounts must approve it first
 */
class MultiSig extends internal_1.Account {
    /**
     * @hidden
     */
    constructor(identifiers, context) {
        super(identifiers, context);
        this.modify = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: modifyArgs => [internal_1.modifyMultiSig, Object.assign({ multiSig: this }, modifyArgs)],
        }, context);
        this.setAdmin = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: adminArgs => [setMultiSigAdmin_1.setMultiSigAdmin, Object.assign({ multiSig: this }, adminArgs)] }, context);
        this.removePayer = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: () => [internal_1.removeMultiSigPayer, { multiSig: this }],
            voidArgs: true,
        }, context);
    }
    /**
     * Return details about this MultiSig such as the signing Accounts and the required number of signatures to execute a MultiSigProposal
     */
    details() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { multiSig }, }, }, context, address, } = this;
            const rawAddress = (0, conversion_1.stringToAccountId)(address, context);
            const [rawSigners, rawSignersRequired] = yield Promise.all([
                multiSig.multiSigSigners.entries(rawAddress),
                multiSig.multiSigSignsRequired(rawAddress),
            ]);
            const signers = rawSigners.map(([{ args: [, signatory], },]) => {
                const signerAddress = (0, conversion_1.accountIdToString)(signatory);
                return new internal_1.Account({ address: signerAddress }, context);
            });
            const requiredSignatures = (0, conversion_1.u64ToBigNumber)(rawSignersRequired);
            return { signers, requiredSignatures };
        });
    }
    /**
     * Given an ID, fetch a { @link api/entities/MultiSigProposal!MultiSigProposal } for this MultiSig
     *
     * @throws if the MultiSigProposal is not found
     */
    getProposal(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { id } = args;
            const { address, context } = this;
            const proposal = new MultiSigProposal_1.MultiSigProposal({ multiSigAddress: address, id }, context);
            const exists = yield proposal.exists();
            if (!exists) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.DataUnavailable,
                    message: `Proposal with ID "${id}" was not found`,
                });
            }
            return proposal;
        });
    }
    /**
     * Return all active { @link api/entities/MultiSigProposal!MultiSigProposal } for this MultiSig Account
     */
    getProposals() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { multiSig }, }, }, context, address, } = this;
            const rawAddress = (0, conversion_1.stringToAccountId)(address, context);
            const rawProposalEntries = yield multiSig.proposals.entries(rawAddress);
            const proposals = [];
            if (!rawProposalEntries.length) {
                return [];
            }
            const queries = rawProposalEntries.map(([{ args: [rawKey, rawId], },]) => {
                proposals.push(new MultiSigProposal_1.MultiSigProposal({ multiSigAddress: address, id: (0, conversion_1.u64ToBigNumber)(rawId) }, context));
                return [rawKey, rawId];
            });
            const details = yield multiSig.proposalStates.multi(queries);
            const statuses = details.map(stateOpt => {
                if (stateOpt.isSome) {
                    const state = stateOpt.unwrap();
                    return (0, conversion_1.meshProposalStateToProposalStatus)(state);
                }
                return types_1.ProposalStatus.Invalid;
            });
            return proposals.filter((_, index) => statuses[index] === types_1.ProposalStatus.Active);
        });
    }
    /**
     * Return a set of { @link api/entities/MultiSigProposal!MultiSigProposal | MultiSigProposal } for this MultiSig Account
     *
     * @note uses the middlewareV2
     */
    getHistoricalProposals(opts) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context, address } = this;
            const { size, start } = opts !== null && opts !== void 0 ? opts : {};
            const { data: { multiSigProposals: { nodes, totalCount }, }, } = yield context.queryMiddleware((0, multisigs_1.multiSigProposalsQuery)(address, size, start));
            const getTxTagAndArgs = (proposal) => ({
                txTag: (0, conversion_1.extrinsicIdentifierToTxTag)({
                    moduleId: proposal.module.toLowerCase(),
                    callId: proposal.call,
                }),
                args: proposal.args,
            });
            const data = nodes.map(({ proposalId, status, approvalCount, rejectionCount, params }) => {
                const { expiry, proposals, isBatch } = params;
                let txTag;
                let args;
                if (isBatch) {
                    txTag = types_1.UtilityTx.Batch;
                    args = proposals.map(getTxTagAndArgs);
                }
                else {
                    ({ txTag, args } = getTxTagAndArgs(proposals[0]));
                }
                return {
                    proposal: new MultiSigProposal_1.MultiSigProposal({ id: new bignumber_js_1.default(proposalId), multiSigAddress: address }, context),
                    status: (0, conversion_1.middlewareProposalStateToProposalStatus)(status),
                    approvalAmount: new bignumber_js_1.default(approvalCount),
                    rejectionAmount: new bignumber_js_1.default(rejectionCount),
                    expiry: expiry ? new Date(+expiry.replaceAll(',', '')) : null,
                    txTag,
                    args,
                };
            });
            const count = new bignumber_js_1.default(totalCount);
            const next = (0, internal_2.calculateNextKey)(count, data.length, start);
            return {
                data,
                next,
                count,
            };
        });
    }
    /**
     * Returns the Identity of the MultiSig admin. This Identity can add or remove signers directly without creating a MultiSigProposal first.
     */
    getAdmin() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { multiSig }, }, }, context, address, } = this;
            const rawAddress = (0, conversion_1.addressToKey)(address, context);
            const rawAdminDid = yield multiSig.adminDid(rawAddress);
            if (rawAdminDid.isNone) {
                return null;
            }
            const did = (0, conversion_1.identityIdToString)(rawAdminDid.unwrap());
            return new internal_1.Identity({ did }, context);
        });
    }
    /**
     * Returns the payer for the MultiSig, if set the primary account of the identity will pay for any fees the MultiSig may incur
     */
    getPayer() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { multiSig }, }, }, context, address, } = this;
            const rawAddress = (0, conversion_1.addressToKey)(address, context);
            const rawPayingDid = yield multiSig.payingDid(rawAddress);
            if (rawPayingDid.isNone) {
                return null;
            }
            const did = (0, conversion_1.identityIdToString)(rawPayingDid.unwrap());
            return new internal_1.Identity({ did }, context);
        });
    }
}
exports.MultiSig = MultiSig;
//# sourceMappingURL=index.js.map